import { map } from 'ramda'
import { ThunkDispatch } from 'redux-thunk'

import { PAYMENT_PROVIDERS } from 'App/constants'
import { PaymentMethod } from 'features/checkout/constants'
import { AuthError } from 'features/checkout/duck/operations/postProceed'
import checkoutSelectors from 'features/checkout/duck/selectors'
import { PollerOptions, createPoller } from 'features/checkout/helpers/poller/poller'
import { getKlarnaSession, postKlarnaError, getKlarnaAuthToken } from 'features/checkout/requests'
import { AuthTokenResponseNormalized, KlarnaAuthResponseNormalized } from 'features/checkout/types'
import {
  setPaymentsAuthStart as setKlarnaPaymentsAuthStart,
  setPaymentsAuthStatus as setKlarnaPaymentsAuthStatus,
} from 'features/klarna/duck/actions'
import klarnaSelectors from 'features/klarna/duck/selectors'
import klarnaPaymentsAuthorize from 'features/klarna/helpers/klarnaPaymentsAuthorize'
import { splitDateOfBirth } from 'helpers/dateOfBirth'
import { RootState } from 'redux/types'

import { KlarnaPostProceedBody, KlarnaGuestPayLaterPostProceedBody } from './types'

interface PrepareKlarnaArgs {
  state: RootState
  dispatch: ThunkDispatch<RootState, undefined, any>
  selectedPaymentMethod: PaymentMethod
}

export async function prepareKlarna({
  state,
  dispatch,
  selectedPaymentMethod,
}: PrepareKlarnaArgs): Promise<KlarnaPostProceedBody | KlarnaGuestPayLaterPostProceedBody> {
  const omnicreditAuthCallbackEnabled = checkoutSelectors.getOmnicreditAuthCallbackEnabled(state)
  dispatch(setKlarnaPaymentsAuthStart())
  // If guest CO, we should send the dateOfBirth to the klarna session
  const isGuest = checkoutSelectors.getIsGuestCustomer(state)
  const memberInfo = klarnaSelectors.getMemberInfo(state)
  const dateOfBirth = isGuest && memberInfo.validated ? memberInfo.dateOfBirth : undefined

  // GET the klarna session update body, it's needed for Klarna.Payments.authorize
  const { sessionId, ...updateSessionBody } = await getKlarnaSession(dateOfBirth)

  // call Klarna.Payments.authorize
  const getKlarnaAuthorisationToken = async (): Promise<KlarnaAuthResponseNormalized> =>
    klarnaPaymentsAuthorize(selectedPaymentMethod, updateSessionBody)

  // call Klarna.Payments.authorize in conjunction with polling to Hybris for Klarna server-side authentication key
  const pollerOptions: PollerOptions<AuthTokenResponseNormalized> = {
    callback: () => getKlarnaAuthToken(sessionId),
    interval: 10000,
    limit: { count: 12 },
    delayedStartMs: 60000,
    // stop polling when retrieve auth token or when klarna auth process finished
    shouldEndPolling: (response) => !!response.serverSideAuthorizationToken,
  }
  const poller = createPoller(pollerOptions)

  // INFO: custom implementation for calling promises in parallel
  const getKlarnaAuthorisationTokenRace = async (): Promise<
    AuthTokenResponseNormalized | KlarnaAuthResponseNormalized
  > => {
    return new Promise((resolve) => {
      getKlarnaAuthorisationToken().then((tokenInfo) => {
        poller.stop()
        resolve(tokenInfo)
      })
      poller.start().then((tokenInfo) => {
        if (tokenInfo) {
          resolve(tokenInfo)
        }
      })
    })
  }

  const klarnaAuthResponse = omnicreditAuthCallbackEnabled
    ? await getKlarnaAuthorisationTokenRace()
    : await getKlarnaAuthorisationToken()

  const klarnaAuthResponseNormalized: KlarnaAuthResponseNormalized =
    'serverSideAuthorizationToken' in klarnaAuthResponse!
      ? {
          authorizationToken: klarnaAuthResponse.serverSideAuthorizationToken,
          approved: true,
          showForm: false,
        }
      : (klarnaAuthResponse as KlarnaAuthResponseNormalized)

  dispatch(
    setKlarnaPaymentsAuthStatus({
      paymentMethodCategory: selectedPaymentMethod,
      response: klarnaAuthResponseNormalized,
    }),
  )

  const { authorizationToken, approved, showForm } = klarnaAuthResponseNormalized
  if (!approved) {
    if (showForm === false) {
      postKlarnaError(selectedPaymentMethod)
    }
    poller.stop()
    const klarnaError: AuthError = new Error('Klarna Payments authorisation not approved')
    klarnaError.code = PAYMENT_PROVIDERS.KLARNA
    throw klarnaError
  }

  const body: KlarnaPostProceedBody = { omnicreditAuthCode: authorizationToken as string }

  // Pay Later requires users to become members
  return isGuest
    ? {
        ...body,
        joinClub: true,
        ...map(Number, splitDateOfBirth(memberInfo.dateOfBirth)),
        password: memberInfo.password,
      }
    : body
}
